import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Sparkles, Eye, EyeOff } from 'lucide-react';
import ThemeToggle from '../components/ThemeToggle';
import SiteFooter from '../components/SiteFooter';
import { toast } from 'sonner';

const Auth = ({ onLogin }) => {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const response = await axios.post(`${API}${endpoint}`, { email, password });
      
      toast.success(isLogin ? 'Welcome back!' : 'Account created successfully!');
      onLogin(response.data.token, response.data.user);
      navigate(response.data.user?.role === 'admin' ? '/admin' : '/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 opacity-20">
        <img 
          src="https://static.prod-images.emergentagent.com/jobs/adfa46bb-e966-4f22-bd70-893aead51158/images/1768afda67958369e963242dcffcfae0909d8fa6e61800801f6e630ac7c90f9e.png"
          alt="Background"
          className="w-full h-full object-cover"
        />
      </div>

      <main className="relative z-10 flex flex-1 items-center justify-center px-6 py-12 sm:py-16">
        <div className="w-full max-w-md">
          {/* Theme Toggle floating top-right */}
          <div className="absolute -top-2 right-0 flex items-center gap-2">
            <ThemeToggle />
          </div>
          {/* Logo */}
          <div className="flex items-center justify-center gap-2 mb-8">
            <Sparkles className="w-8 h-8 text-[#D4AF37]" strokeWidth={1.5} />
            <span className="text-3xl font-heading font-light tracking-tight">Seatask Arcane AI</span>
          </div>

        {/* Auth Card */}
        <div className="glass-effect rounded-lg p-8" data-testid="auth-form">
          <h2 className="text-3xl font-heading font-medium mb-2 text-center">
            {isLogin ? 'Welcome Back' : 'Begin Your Journey'}
          </h2>
          <p className="text-zinc-400 text-center mb-8 text-sm">
            {isLogin ? 'Sign in to access your readings' : 'Create an account to start'}
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-body text-zinc-300 mb-2">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="email-input"
                required
                className="w-full px-4 py-3 bg-[#0F0F11] border border-white/10 rounded-md text-[#F8F9FA] focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/40 transition-all"
                placeholder="your@email.com"
              />
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-body text-zinc-300 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="password-input"
                  required
                  className="w-full px-4 py-3 bg-[#0F0F11] border border-white/10 rounded-md text-[#F8F9FA] focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/40 transition-all"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  data-testid="toggle-password-visibility"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-200 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              data-testid="submit-auth-button"
              className="w-full py-3 bg-[#D4AF37] text-[#050505] rounded-md hover:bg-[#F3E5AB] transition-all duration-300 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Please wait...' : (isLogin ? 'Sign In' : 'Create Account')}
            </button>
          </form>

          {/* Toggle */}
          <div className="mt-6 text-center">
            <button
              onClick={() => setIsLogin(!isLogin)}
              data-testid="toggle-auth-mode"
              className="text-sm text-zinc-400 hover:text-[#D4AF37] transition-colors"
            >
              {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
            </button>
          </div>
        </div>

        {/* Back to Home */}
        <div className="mt-6 text-center">
          <button
            onClick={() => navigate('/')}
            data-testid="back-to-home-button"
            className="text-sm text-zinc-500 hover:text-zinc-300 transition-colors"
          >
            ← Back to Home
          </button>
        </div>
        </div>
      </main>
      <SiteFooter className="mt-auto border-white/5 py-4" />
    </div>
  );
};

export default Auth;
